import {
  ADDRESS_BUILDING,
  ADDRESS_CITY,
  ADDRESS_COORDINATES,
  ADDRESS_COUNTRY,
  ADDRESS_POST_CODE,
  ADDRESS_STATE,
  ADDRESS_STREET,
  BUSINESS_UNIT_TYPE,
  CODE,
  DATE_CLOSED,
  DATE_OPENED,
  EQUITY_SHAREHOLDING,
  EU_TAXONOMY_CODE,
  FINANCIAL_CONTROL,
  NACE_CODE,
  NACE_SECTION,
  NAME,
  OWNERSHIP,
  PRIORITY_RATING,
  PROFIT_CENTER_LINKS,
  UNIQUE_IDENTIFIER,
  VALUE_CHAIN_LIST,
  VALUE_CHAIN_SECTION,
} from 'constants/formFields';
import { DATA_SETS_DATE_FORMAT, VC_BOARD_TYPES } from 'constants/general';
import dayjs from 'dayjs';
import profitCenterLinksFormatted from 'utils/profitCenterLinksFormatted';

import getFormValue from '../getFormValue';
import getNaceLabel from '../getNaceLabel';

import type { IGetCustomerDefaultValues } from './interfaces/IGetCustomerDefaultValues';

function getCustomerDefaultValues({
  values, naceCodeTreeData, locationsCountries, valueChainSectionCurrent,
}: IGetCustomerDefaultValues) {
  const {
    name,
    code,
    addressBuilding,
    addressCity,
    addressCountry,
    addressPostCode,
    addressState,
    addressStreet,
    businessUnitType,
    dateClosed,
    dateOpened,
    naceCode,
    equityShareholding,
    financialControl,
    ownership,
    uniqueIdentifier,
    valueChainSection,
    naceSection,
    addressCoordinates,
    priorityRating,
    profitCenterLinks,
    euTaxonomyCode,
  } = values || {};

  const defaultOption = undefined;

  const { naceSectionLabel, naceDvisions, naceCodeLabel } = getNaceLabel(
    { naceCode: naceCode || '', naceSection: naceSection || '', naceCodeTreeData },
  );

  const locationsCountriesOptions = (countryCode: string) => {
    const country = locationsCountries?.find((countryItem) => countryItem.iso2 === countryCode);
    return {
      value: country?.iso2 || '',
      label: country?.name || '',
    };
  };

  return {
    [NAME]: name || '',
    [CODE]: code || '',
    [UNIQUE_IDENTIFIER]: uniqueIdentifier || '',
    [VALUE_CHAIN_SECTION]: {
      value: valueChainSectionCurrent || valueChainSection?.split('. ')[1],
      label: valueChainSectionCurrent || valueChainSection?.split('. ')[1],
    },
    [VALUE_CHAIN_LIST]: {
      value: VC_BOARD_TYPES.DOWNSTREAM,
      label: VC_BOARD_TYPES.DOWNSTREAM,
    },
    [OWNERSHIP]: ownership ? {
      value: ownership, label: ownership,
    } : defaultOption,
    [EQUITY_SHAREHOLDING]: getFormValue({ value: equityShareholding }),
    [FINANCIAL_CONTROL]: getFormValue({ value: financialControl }),
    [BUSINESS_UNIT_TYPE]: businessUnitType ? {
      value: businessUnitType, label: businessUnitType,
    } : defaultOption,
    [PRIORITY_RATING]: priorityRating ? {
      value: priorityRating, label: priorityRating,
    } : defaultOption,
    [DATE_OPENED]: dateOpened ? dayjs(dateOpened).format(DATA_SETS_DATE_FORMAT) : '',
    [DATE_CLOSED]: dateClosed ? dayjs(dateClosed).format(DATA_SETS_DATE_FORMAT) : '',
    [NACE_SECTION]: naceSection ? {
      value: naceSection,
      label: naceSectionLabel || '',
      divisions: naceDvisions,
    } : defaultOption,
    [NACE_CODE]: naceCode ? {
      value: naceCode,
      label: naceCodeLabel || '',
    } : defaultOption,
    [EU_TAXONOMY_CODE]: euTaxonomyCode || '',
    [ADDRESS_COUNTRY]: addressCountry ? locationsCountriesOptions(addressCountry) : defaultOption,
    [ADDRESS_STATE]: addressState ? {
      value: addressState, label: addressState,
    } : defaultOption,
    [ADDRESS_POST_CODE]: addressPostCode || '',
    [ADDRESS_CITY]: addressCity ? {
      value: addressCity, label: addressCity,
    } : defaultOption,
    [ADDRESS_STREET]: addressStreet || '',
    [ADDRESS_BUILDING]: addressBuilding || '',
    [ADDRESS_COORDINATES]: addressCoordinates || '',
    [PROFIT_CENTER_LINKS]: profitCenterLinksFormatted(profitCenterLinks || []),
  };
}

export default getCustomerDefaultValues;
